// @vendors
import { useContext, useMemo } from 'react'
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup'
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Form, Formik } from 'formik';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment'

// @components material
import {
    AddIcon,
    Box,
    Button,
    ClearAllIcon,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    SearchRoundedIcon,
    Select,
    TextField,
    Tooltip,
 } from '../../../components/shared/MaterialUI';

// @assets
import { GeneralStyles } from '../../../assets/css/general-style';

// @constants
import { ROUTENAME } from '../../../navigation/RouteName';

// @helpers
import { listStatusTickets, MenuItemProps, validateObject } from '../../../helpers/FunctionsUtils';

// @assets
import { theme } from '../../../assets/theme';

// @context
import { TicketContext } from '../TicketManagement';

const ToolbarTicket = () => {
  const generalStyle = useMemo(() => GeneralStyles(), []);
  const history = useHistory();
  const {
    handleMapResponse,
    filterTicket,
    setFilterTicket,
    setSortBy,
    sortBy
  } = useContext(TicketContext);

  const validationSchemas = Yup.object({
    status: Yup.string().notRequired().nullable(),
    updatedAt: Yup.date()
    .nullable()
    .notRequired()
    .typeError('Invalid date, "MM/dd/yyyy" format'),
    createdAt: Yup.date()
    .nullable()
    .notRequired()
    .typeError('Invalid date, "MM/dd/yyyy" format')
  });
  
  const handleValue = (date, field) => {
    if(field === 'createdAt'){
      setFilterTicket(prev => ({...prev, createdAt: date}));
      return;
    }
    return setFilterTicket(prev => ({...prev, updatedAt: date}));
  }

  const handleChange = (e) => {
    const value = e.target.value;
    setFilterTicket(prev => ({...prev, status: value}));
  }

  const handleSubmit = () => {
    const filterHasValue = validateObject(filterTicket);
    if(!filterHasValue)return;
    const params = {
      filters: {
        status: filterTicket.status,
        createdAt: filterTicket.createdAt ? moment(filterTicket.createdAt).format("MM/DD/YYYY") : null,
        updatedAt: filterTicket.updatedAt ? moment(filterTicket.updatedAt).format("MM/DD/YYYY") : null
      },
      orderBy: {sort: sortBy.sort, direction: sortBy.direction}
    };
    handleMapResponse(params);
  }

  const clearSearch = () => {
    const filterHasValue = validateObject(filterTicket);
    if(!filterHasValue)return;
    setFilterTicket({
      status: null,
      updatedAt: null,
      createdAt: null
    });
    setSortBy({
      sort: '',
      direction: ''
    })
    handleMapResponse();
  }

  return (
    <Box sx={generalStyle.containerToolbar}>
      <Grid sx={{
        ...generalStyle.gridToolbar,
        [theme.breakpoints.down('md')]: {
          flexDirection: 'column'
         }
        }}
      >
        <Grid>
          <h2 style={{color: theme.palette.primary.black}}>Tickets</h2>
        </Grid>

        <Grid sx={{width: '100%'}}>
          <Formik
            initialValues={filterTicket}
            validationSchema={validationSchemas}
            onSubmit={() => {handleSubmit()}}
            enableReinitialize={true}
          >
            {() => {
              return (
                <Form autoComplete="off" className="formCreateUser">
                  <Grid
                    sx={{
                      display: 'flex',
                      gap: '15px',
                      width: '100%',
                      padding: '0px 20px',
                      [theme.breakpoints.down('md')]: {
                        padding: '0px',
                        flexDirection: 'column',
                        alignItems: 'center'
                      }
                    }}
                  >
                    <Box sx={{width: '100%'}}>
                      <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={navigator.language}>
                        <DesktopDatePicker
                          value={filterTicket.createdAt}
                          onChange={(date) => {handleValue(date, 'createdAt')}}                
                          allowSameDateSelection={true}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              sx={{...generalStyle.input, ...generalStyle.customButtonSm}}
                              value={filterTicket.createdAt}
                              id="createdAt"
                              name="createdAt"
                              label="Created At"
                              defaultValue={null}
                              fullWidth
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Box>
                    
                    <Box sx={{width: '100%'}}>
                      <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={navigator.language}>
                        <DesktopDatePicker
                          value={filterTicket.updatedAt}
                          onChange={(date) => {handleValue(date, 'updatedAt')}}                
                          allowSameDateSelection={true}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              sx={{...generalStyle.input, ...generalStyle.customButtonSm}}
                              value={filterTicket.updatedAt}
                              id="updatedAt"
                              name="updatedAt"
                              label="Updated At"
                              defaultValue={null}
                              fullWidth
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Box>
                    
                    <Box sx={{width: '100%'}}>
                      <FormControl fullWidth>
                        <InputLabel id="demo-simple-select-label">Status</InputLabel>
                        <Select
                          id="demo-simple-select"
                          labelId="demo-simple-select-label"
                          label="Status"
                          name='priority'
                          value={filterTicket.status}
                          sx={{...generalStyle.input, ...generalStyle.customButtonSm}}
                          onChange={(e) => {handleChange(e);}}
                          MenuProps={MenuItemProps}
                        >
                            {listStatusTickets?.map((item, index) => (
                                <MenuItem
                                    value={item.id}
                                    key={index}
                                >
                                    {item.value}
                                </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Box>

                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                      <Tooltip title="Search">
                        <IconButton
                          type='submit'
                          sx={generalStyle.iconButtonSearch }  
                        >
                          <SearchRoundedIcon />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Clear filter">
                        <IconButton
                          onClick={() => clearSearch()}
                          sx={generalStyle.iconButtonSearch}
                        >
                          <ClearAllIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        </Grid>

        <Grid
            sx={{
            [theme.breakpoints.down('md')]: {
              width:'100%',
              marginTop: '20px'
            }
          }}
        >
          <Button
            endIcon={<AddIcon />}
            sx={{
              ...generalStyle.customButtonMain,
              width:'200px',
              ...generalStyle.customButtonSm,
              
            }}
            onClick={() => history.push(ROUTENAME.createTicket)}
          >
            Add Ticket
          </Button>
        </Grid>  
      </Grid>
    </Box>
  )
}

export default ToolbarTicket